/**
 * App shell: providers + router + auth gate.
 *
 * Providers stack (outer → inner):
 *   - QueryClientProvider — server cache
 *   - TonConnectUIProvider — wallet UI + status
 *   - AuthProvider — drives the gate state machine
 *
 * The router (wouter) only mounts inside AuthProvider's "ready" branch — the
 * pre-auth UI is purely the ConnectPage, no routes needed.
 *
 * Deep links (`t.me/TolkgramBot?startapp=compose__<address>`, per architecture
 * Section 9.15) are parsed from `tgWebAppStartParam` on first auth-ready
 * render and route the user straight into a thread.
 */
import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TonConnectUIProvider } from "@tonconnect/ui-react";
import { Route, Switch, useLocation } from "wouter";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { useStreamSync } from "./hooks/useStream";
import { ConnectPage } from "./pages/ConnectPage";
import { InboxPage } from "./pages/InboxPage";
import { ThreadPage } from "./pages/ThreadPage";
import { env } from "./lib/env";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, err) => {
        // Don't retry 4xx; do retry network errors up to 2x.
        if (err instanceof Error && /HTTP 4\d\d/.test(err.message)) return false;
        return failureCount < 2;
      },
      staleTime: 10_000,
      refetchOnWindowFocus: true,
    },
    mutations: { retry: 0 },
  },
});

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <TonConnectUIProvider
        manifestUrl={env.tonconnectManifestUrl}
        actionsConfiguration={
          env.twaReturnUrl !== undefined
            ? { twaReturnUrl: env.twaReturnUrl as `${string}://${string}` }
            : undefined
        }
      >
        <AuthProvider>
          <AuthGatedRouter />
        </AuthProvider>
      </TonConnectUIProvider>
    </QueryClientProvider>
  );
}

function AuthGatedRouter(): JSX.Element {
  const { status } = useAuth();

  if (status !== "ready") {
    return <ConnectPage />;
  }

  return <SignedInApp />;
}

function SignedInApp(): JSX.Element {
  // Wire SSE → cache + pending store. Only matters when we have a JWT,
  // which is implied by status='ready'.
  useStreamSync();

  // Handle compose deep link once on mount.
  useDeepLinkRoute();

  return (
    <Switch>
      <Route path="/" component={InboxPage} />
      <Route path="/thread/:counterparty">
        {({ counterparty }: { counterparty: string }) => (
          <ThreadPage counterparty={decodeURIComponent(counterparty)} />
        )}
      </Route>
      <Route>
        <NotFound />
      </Route>
    </Switch>
  );
}

function NotFound(): JSX.Element {
  const [, setLocation] = useLocation();
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 px-8 text-center">
      <p className="text-sm text-tg-hint">Page not found.</p>
      <button
        onClick={() => setLocation("/")}
        className="rounded-md bg-tg-button px-4 py-2 text-sm font-medium text-tg-button-text"
      >
        Back to inbox
      </button>
    </div>
  );
}

/**
 * Deep-link handler.
 *
 * Telegram passes the `startapp` payload as `tgWebAppStartParam` on the
 * launch URL hash. Format we support: `compose__<address>` — opens straight
 * into the thread with that counterparty so the user can compose. Anything
 * we don't understand: ignore.
 */
function useDeepLinkRoute(): void {
  const [, setLocation] = useLocation();
  useEffect(() => {
    try {
      const hash = window.location.hash.slice(1);
      const params = new URLSearchParams(hash);
      const start = params.get("tgWebAppStartParam");
      if (start === null) return;
      if (start.startsWith("compose__")) {
        const addr = start.slice("compose__".length);
        if (addr.length > 0) {
          setLocation(`/thread/${encodeURIComponent(addr)}`);
        }
      }
    } catch {
      /* ignore — malformed hash isn't worth crashing over */
    }
    // Only run once per signed-in session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
